<template>
  <div>
    <!-- Topo -->
    <div class="dtop">
      <h1>Agenda</h1>
      <div class="row" style="gap:var(--space-2);align-items:center">
        <button class="btn btn--ghost btn--sm" @click="prevWeek">‹</button>
        <span class="sr-week-label">{{ weekLabel }}</span>
        <button class="btn btn--ghost btn--sm" @click="nextWeek">›</button>
        <button class="btn btn--outline btn--sm" @click="goToToday">
          Hoje
        </button>
        <button
          class="btn btn--ghost btn--sm sr-reconfig"
          @click="$bvModal.show('reconfig-confirm-modal')"
        >
          Reconfigurar
        </button>
      </div>
      <button
        class="btn btn--accent btn--sm sr-btn-novo"
        @click="$bvModal.show('punctual-slot-modal')"
      >
        + Novo horário
      </button>
    </div>

    <!-- Modal confirmação reconfigurar agenda -->
    <b-modal
      id="reconfig-confirm-modal"
      hide-header
      hide-footer
      centered
      size="sm"
    >
      <div class="rc-body">
        <div class="rc-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 9v4M12 17h.01"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
            <path
              d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <p class="rc-title">Reconfigurar agenda?</p>
        <p class="rc-text">
          Suas configurações atuais serão desfeitas e você deverá configurar a
          agenda novamente. As consultas já agendadas
          <strong>não serão desmarcadas</strong>
          .
        </p>
        <div class="rc-footer">
          <button
            class="btn btn--ghost btn--sm"
            @click="$bvModal.hide('reconfig-confirm-modal')"
          >
            Cancelar
          </button>
          <button
            class="btn btn--primary btn--sm"
            @click="confirmDeleteSchedule"
          >
            Continuar
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Modal horário pontual (BootstrapVue — appended to body) -->
    <b-modal
      id="punctual-slot-modal"
      title="Novo horário pontual"
      hide-footer
      centered
    >
      <div class="pm-body">
        <div class="pm-row">
          <div class="pm-field">
            <label class="pm-label">Data</label>
            <input
              class="pm-input"
              type="date"
              v-model="punctual.date"
              :min="minPunctualDate"
            />
          </div>
          <div class="pm-field">
            <label class="pm-label">A partir das</label>
            <input class="pm-input" type="time" v-model="punctual.start_time" />
          </div>
        </div>
        <div class="pm-field">
          <label class="pm-label">Quantos slots</label>
          <div class="pm-slots-row">
            <button
              v-for="n in [1, 2, 3, 4, 5]"
              :key="n"
              type="button"
              class="pm-slot-btn"
              :class="{ 'pm-slot-btn--active': punctual.n_slots === n }"
              @click="punctual.n_slots = n"
            >
              {{ n }}
            </button>
          </div>
        </div>
        <div v-if="punctual.date && punctual.start_time" class="pm-preview">
          {{ punctual.n_slots }} slot{{ punctual.n_slots > 1 ? "s" : "" }} de
          {{ configSlot.duration || 30 }} min · das {{ punctual.start_time }} às
          {{ punctualEndTime }}
        </div>
      </div>
      <div class="pm-footer">
        <button
          class="btn btn--ghost btn--sm"
          @click="$bvModal.hide('punctual-slot-modal')"
        >
          Cancelar
        </button>
        <button
          class="btn btn--primary btn--sm"
          :disabled="canSavePunctual || isSavingPunctual"
          @click="savePunctualSlot"
        >
          {{ isSavingPunctual ? "Salvando…" : "Salvar horário" }}
        </button>
      </div>
    </b-modal>

    <div class="sr-body">
      <!-- 3 métricas -->
      <div class="metrics3">
        <div class="card" style="padding:var(--space-5)">
          <span class="metric-label">Consultas hoje</span>
          <span class="metric-value">{{ todayCount }}</span>
          <span v-if="nextTodayApt" class="metric-sub metric-sub--good">
            próxima em breve
          </span>
          <span v-else class="metric-sub">hoje</span>
        </div>
        <div class="card" style="padding:var(--space-5)">
          <span class="metric-label">Ocupação da semana</span>
          <span class="metric-value">{{ weekOccupancyPct }}%</span>
          <span class="metric-sub">{{ weekOccupancyLabel }}</span>
        </div>
        <router-link
          to="/financeiro"
          class="card"
          style="padding:var(--space-5);text-decoration:none;display:flex;flex-direction:column;gap:var(--space-1);cursor:pointer"
        >
          <span class="metric-label">Receita prevista (semana)</span>
          <span class="metric-value money--pos">
            {{
              weekForecastCents !== null
                ? intToBRL(weekForecastCents, true)
                : "—"
            }}
          </span>
          <span
            class="metric-sub"
            style="color:var(--color-primary);font-weight:var(--fw-bold)"
          >
            Ver financeiro →
          </span>
        </router-link>
      </div>

      <!-- Card configuração da agenda -->
      <div
        v-if="configSlot && configSlot.work_days"
        class="card sr-config-card"
      >
        <p class="metric-label">Configuração da agenda</p>
        <div class="sr-config-grid">
          <div class="sr-config-item">
            <span class="sr-config-label">Período</span>
            <span class="sr-config-value">
              {{ formatConfigSlotDate(configSlot.from_date, "dd/MM/yyyy") }}
              –
              {{ formatConfigSlotDate(configSlot.until_date, "dd/MM/yyyy") }}
            </span>
          </div>
          <div class="sr-config-item">
            <span class="sr-config-label">Dias</span>
            <span class="sr-config-value">{{ workDays || "—" }}</span>
          </div>
          <div
            v-if="
              configSlot.work_days[0] &&
                configSlot.work_days[0].times &&
                configSlot.work_days[0].times[0]
            "
            class="sr-config-item"
          >
            <span class="sr-config-label">Horário</span>
            <span class="sr-config-value">
              {{ configSlot.work_days[0].times[0].start_time }}
              –
              {{ configSlot.work_days[0].times[0].end_time }}
            </span>
          </div>
          <div class="sr-config-item">
            <span class="sr-config-label">Duração</span>
            <span class="sr-config-value">{{ configSlot.duration }} min</span>
          </div>
        </div>
      </div>

      <!-- Grade semanal -->
      <div class="sr-week">
        <div v-for="day in weekDays" :key="day.isoDate" class="sr-wcol">
          <div
            class="sr-wcol-head"
            :class="{ 'sr-wcol-head--today': day.isToday }"
          >
            <div class="sr-dn" :class="{ 'sr-dn--today': day.isToday }">
              {{ day.dayName }}
            </div>
            <div class="sr-dd" :class="{ 'sr-dd--today': day.isToday }">
              {{ day.dayNumber }}
            </div>
          </div>
          <div class="sr-wcol-body">
            <component
              v-for="apt in appointmentsForDay(day.isoDate)"
              :key="apt.id"
              :is="isNowApt(apt) && apt.room ? 'router-link' : 'div'"
              :to="isNowApt(apt) && apt.room ? aptRoomRoute(apt) : undefined"
              class="sr-ap"
              :class="aptClass(apt)"
            >
              <div class="sr-apt">{{ aptTimeLabel(apt) }}</div>
              <div class="sr-apn">
                {{ aptPatientName(apt) }}{{ isNowApt(apt) ? " →" : "" }}
              </div>
            </component>
            <div
              v-if="!appointmentsForDay(day.isoDate).length"
              class="sr-ap sr-ap--free"
            >
              <div class="sr-apt">Livre</div>
              <div>Disponível</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Legenda -->
      <div class="row" style="gap:var(--space-5);flex-wrap:wrap">
        <span class="leg">
          <span class="leg-swatch leg-swatch--book" />
          Agendada
        </span>
        <span class="leg">
          <span class="leg-swatch leg-swatch--now" />
          Agora / em breve
        </span>
        <span class="leg">
          <span class="leg-swatch leg-swatch--done" />
          Realizada
        </span>
        <span class="leg">
          <span class="leg-swatch leg-swatch--free" />
          Disponível
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  slotMethods,
  appointmentsMethods,
  userComputed
} from "./../state/helpers";
import { Doctor } from "./../services/doctor";
import { intToBRL } from "./../utils/currency/convert";
import {
  startOfWeek,
  addDays,
  addWeeks,
  format,
  isToday,
  isSameDay,
  parseISO,
  isPast,
  differenceInMinutes
} from "date-fns";
import { pt as locale } from "date-fns/locale";
import { formatDate } from "./../utils/masks/formatSlotDate";

export default {
  name: "ScheduleResume",
  props: {
    doctorId: { type: String, default: "" },
    unitId: { type: String, default: "" }
  },
  data() {
    return {
      configSlot: {},
      weekOffset: 0,
      isSavingPunctual: false,
      punctual: {
        date: "",
        start_time: "",
        n_slots: 1
      },
      days: [
        { name: "sunday", translate: "Domingo", day_of_week: 0 },
        { name: "monday", translate: "Segunda", day_of_week: 1 },
        { name: "tuesday", translate: "Terça", day_of_week: 2 },
        { name: "wednesday", translate: "Quarta", day_of_week: 3 },
        { name: "thursday", translate: "Quinta", day_of_week: 4 },
        { name: "friday", translate: "Sexta", day_of_week: 5 },
        { name: "saturday", translate: "Sábado", day_of_week: 6 }
      ],
      weekForecastCents: null
    };
  },
  mounted() {
    this.getConfig(`doctor_id=${this.doctorId}&unit_id=${this.unitId}`).then(
      response => {
        if (response.data.schedule_config !== null) {
          this.configSlot = response.data.schedule_config;
        }
      }
    );
    this.fetchFutureAppointments();
    this.fetchPastAppointments();
    this.loadWeekForecast();
  },
  methods: {
    intToBRL,
    // Receita prevista é do médico logado (endpoint current-user). Só carrega
    // quando ele está vendo a própria agenda; para outro médico fica "—".
    loadWeekForecast() {
      if (!this.currentUser || this.currentUser.id !== this.doctorId) return;
      Doctor.earnings()
        .then(res => {
          this.weekForecastCents = res.data.week_forecast_cents;
        })
        .catch(() => {});
    },
    ...slotMethods,
    ...appointmentsMethods,
    prevWeek() {
      this.weekOffset--;
    },
    nextWeek() {
      this.weekOffset++;
    },
    goToToday() {
      this.weekOffset = 0;
    },
    formatConfigSlotDate(date, fmt) {
      if (!date) return "";
      if (date.includes("/")) {
        const parts = date.split("/");
        return formatDate(`${parts[2]}-${parts[1]}-${parts[0]}`, fmt);
      }
      return formatDate(date, fmt);
    },
    confirmDeleteSchedule() {
      this.$bvModal.hide("reconfig-confirm-modal");
      this.deleteSlot(`doctor_id=${this.doctorId}&unit_id=${this.unitId}`);
      this.$store.commit("slot/SET_HAS_SLOTS", false);
      this.$store.commit("slot/SET_CREATING_SCHEDULE", true);
      if (this.$route.name !== "ScheduleSettings") {
        this.$router.push({ name: "ScheduleSettings" });
      }
    },
    deleteSchedule() {
      this.$bvModal.show("reconfig-confirm-modal");
    },
    appointmentsForDay(isoDate) {
      return this.allWeekAppointments.filter(apt => {
        if (!apt.scheduled_at) return false;
        try {
          return isSameDay(parseISO(apt.scheduled_at), parseISO(isoDate));
        } catch {
          return false;
        }
      });
    },
    isNowApt(apt) {
      if (!apt.scheduled_at) return false;
      try {
        const mins = differenceInMinutes(
          parseISO(apt.scheduled_at),
          new Date()
        );
        return mins >= -30 && mins <= 30;
      } catch {
        return false;
      }
    },
    isFinishedApt(apt) {
      // Encerrada de verdade pelo médico (ended_at) — vale mesmo que o horário
      // marcado ainda esteja no futuro (encerrada antes da hora).
      if (apt.ended_at) return true;
      if (!apt.scheduled_at) return false;
      try {
        return !this.isNowApt(apt) && isPast(parseISO(apt.scheduled_at));
      } catch {
        return false;
      }
    },
    aptClass(apt) {
      if (this.isFinishedApt(apt)) return "sr-ap--done";
      if (this.isNowApt(apt)) return "sr-ap--now";
      return "sr-ap--book";
    },
    aptTime(apt) {
      if (!apt.scheduled_at) return "";
      try {
        return format(parseISO(apt.scheduled_at), "HH:mm");
      } catch {
        return "";
      }
    },
    aptTimeLabel(apt) {
      const base = this.aptTime(apt);
      if (this.isFinishedApt(apt) || !this.isNowApt(apt)) return base;
      try {
        const mins = differenceInMinutes(
          parseISO(apt.scheduled_at),
          new Date()
        );
        if (mins > 0) return `${base} · em ${mins}min`;
        if (mins === 0) return `${base} · agora`;
        return `${base} · iniciada`;
      } catch {
        return base;
      }
    },
    aptRoomRoute(apt) {
      return apt.room
        ? { path: `/consultoriodaily/sala/${apt.room.id}` }
        : null;
    },
    aptPatientName(apt) {
      const name = apt.patient?.name || apt.name || "";
      const parts = name.split(" ");
      return parts.length > 1 ? `${parts[0]} ${parts[1].charAt(0)}.` : name;
    },
    buildPunctualPayload() {
      const date = this.punctual.date;
      const dayOfWeek = new Date(`${date}T12:00:00`).getDay();
      const duration = this.configSlot?.duration || 30;
      const [h, m] = this.punctual.start_time.split(":").map(Number);
      const endMinutes = h * 60 + m + this.punctual.n_slots * duration;
      const endTime = `${String(Math.floor(endMinutes / 60)).padStart(
        2,
        "0"
      )}:${String(endMinutes % 60).padStart(2, "0")}`;
      return {
        // Soma à agenda: a API não desativa a grade nem troca a configuração.
        punctual: true,
        from_date: date,
        until_date: date,
        duration,
        work_days: [
          {
            day_of_week: dayOfWeek,
            times: [{ start_time: this.punctual.start_time, end_time: endTime }]
          }
        ]
      };
    },
    // 422 de conflito traz a mensagem pronta da API (errors.base).
    punctualErrorMessage(error) {
      const errors = error?.response?.data?.errors;
      const base = errors && Array.isArray(errors.base) ? errors.base[0] : null;
      return base || "Erro ao criar horário. Tente novamente.";
    },
    savePunctualSlot() {
      this.isSavingPunctual = true;
      const payload = this.buildPunctualPayload();
      return this.createSlot({ payload })
        .then(() => {
          this.$bvModal.hide("punctual-slot-modal");
          this.punctual = { date: "", start_time: "", n_slots: 1 };
          this.$bvToast.toast("Horário pontual criado com sucesso.", {
            title: "Horário criado",
            variant: "success",
            solid: true,
            autoHideDelay: 4000
          });
        })
        .catch(error => {
          this.$bvToast.toast(this.punctualErrorMessage(error), {
            title: "Erro",
            variant: "danger",
            solid: true,
            autoHideDelay: 4000
          });
        })
        .finally(() => {
          this.isSavingPunctual = false;
        });
    }
  },
  computed: {
    ...userComputed,
    weekStart() {
      return addWeeks(
        startOfWeek(new Date(), { weekStartsOn: 1 }),
        this.weekOffset
      );
    },
    weekDays() {
      return [0, 1, 2, 3, 4].map(i => {
        const date = addDays(this.weekStart, i);
        const dayStr = format(date, "EEE", { locale });
        return {
          date,
          isoDate: format(date, "yyyy-MM-dd"),
          dayName: dayStr.charAt(0).toUpperCase() + dayStr.slice(1),
          dayNumber: format(date, "d"),
          isToday: isToday(date)
        };
      });
    },
    weekLabel() {
      const start = this.weekDays[0];
      const end = this.weekDays[4];
      const monthYear = format(this.weekStart, "MMMM yyyy", { locale });
      return `Semana de ${start.dayNumber} – ${end.dayNumber} de ${monthYear}`;
    },
    futureList() {
      return this.$store.state.appointments.futureAppointmentsList || [];
    },
    pastList() {
      return this.$store.state.appointments.pastAppointmentsList || [];
    },
    allWeekAppointments() {
      const weekEnd = addDays(this.weekStart, 5);
      const all = [...this.pastList, ...this.futureList];
      return all.filter(apt => {
        if (!apt.scheduled_at) return false;
        try {
          const date = parseISO(apt.scheduled_at);
          return date >= this.weekStart && date < weekEnd;
        } catch {
          return false;
        }
      });
    },
    todayCount() {
      return this.futureList.filter(apt => {
        if (!apt.scheduled_at) return false;
        try {
          return isToday(parseISO(apt.scheduled_at));
        } catch {
          return false;
        }
      }).length;
    },
    nextTodayApt() {
      return this.futureList.find(apt => {
        if (!apt.scheduled_at) return false;
        try {
          const date = parseISO(apt.scheduled_at);
          const mins = differenceInMinutes(date, new Date());
          return isToday(date) && mins >= 0 && mins <= 60;
        } catch {
          return false;
        }
      });
    },
    totalWeekSlots() {
      return Math.max((this.configSlot.work_days?.length || 5) * 4, 1);
    },
    weekOccupancyPct() {
      return Math.min(
        Math.round(
          (this.allWeekAppointments.length / this.totalWeekSlots) * 100
        ),
        100
      );
    },
    weekOccupancyLabel() {
      return `${this.allWeekAppointments.length} de ${this.totalWeekSlots} horários`;
    },
    workDays() {
      if (!this.configSlot.work_days) return "";
      return this.configSlot.work_days
        .map(
          day =>
            this.days.find(d => d.day_of_week === day.day_of_week)?.translate
        )
        .filter(Boolean)
        .join(", ");
    },
    workDaysCount() {
      const days = this.configSlot.work_days;
      return days != null ? days.length : "—";
    },
    startAtLabel() {
      return isPast(this.configSlot.from_date) ? "Iniciado em" : "Inicia em";
    },
    canSavePunctual() {
      return !this.punctual.date || !this.punctual.start_time;
    },
    minPunctualDate() {
      return format(new Date(), "yyyy-MM-dd");
    },
    punctualEndTime() {
      if (!this.punctual.start_time) return "";
      const duration = this.configSlot?.duration || 30;
      const [h, m] = this.punctual.start_time.split(":").map(Number);
      const end = h * 60 + m + this.punctual.n_slots * duration;
      return `${String(Math.floor(end / 60)).padStart(2, "0")}:${String(
        end % 60
      ).padStart(2, "0")}`;
    }
  }
};
</script>

<style lang="scss" scoped>
// ── Topo ─────────────────────────────────────────────────────────────────
.dtop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-8);
  border-bottom: 1px solid var(--color-gray-100);

  h1 {
    margin: 0;
    font-size: var(--font-size-lg);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-900);
  }
}

.sr-week-label {
  font-size: var(--font-size-sm);
  font-weight: var(--fw-bold);
  color: var(--color-gray-700);
  white-space: nowrap;
}

.sr-reconfig {
  color: var(--color-gray-400);
  font-size: var(--font-size-xs);
}

// ── Corpo ─────────────────────────────────────────────────────────────────
.sr-body {
  padding: var(--space-6) var(--space-8) var(--space-12);
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

// ── Métricas ──────────────────────────────────────────────────────────────
.metrics3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-4);
}

.metric-label {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: block;
  margin-bottom: var(--space-1);
}

.metric-value {
  font-size: var(--font-size-3xl);
  font-weight: var(--fw-bold);
  color: var(--color-gray-900);
  font-family: var(--font-num);
  display: block;
  line-height: 1.1;
}

.metric-sub {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
  display: block;
  margin-top: var(--space-1);

  &--good {
    color: var(--color-success);
    font-weight: var(--fw-bold);
  }
}

// ── Grade semanal ─────────────────────────────────────────────────────────
.sr-week {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--space-3);
}

.sr-wcol {
  background: #fff;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.sr-wcol-head {
  padding: var(--space-3);
  text-align: center;
  border-bottom: 1px solid var(--color-gray-100);

  &--today {
    background: var(--color-primary-light);
  }
}

.sr-dn {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-bold);
  color: var(--color-gray-500);
  text-transform: uppercase;

  &--today {
    color: var(--color-primary-dark);
  }
}

.sr-dd {
  font-family: var(--font-num);
  font-size: var(--font-size-xl);
  font-weight: var(--fw-bold);
  color: var(--color-gray-800);

  &--today {
    color: var(--color-primary-dark);
  }
}

.sr-wcol-body {
  padding: var(--space-2);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

// ── Blocos de consulta ────────────────────────────────────────────────────
.sr-ap {
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-xs);
  line-height: 1.3;
  cursor: pointer;
  transition: opacity 0.12s;
  text-decoration: none;

  &:hover {
    opacity: 0.85;
  }

  &--book {
    background: var(--color-primary-light);
    color: var(--color-primary-dark);
    border-left: 3px solid var(--color-primary);
  }

  &--now {
    background: var(--color-success-light);
    color: var(--color-success);
    border: 1.5px solid var(--color-success);
  }

  &--done {
    background: var(--color-gray-50);
    color: var(--color-gray-400);
    border-left: 3px solid var(--color-gray-300);
    cursor: default;

    &:hover {
      opacity: 1;
    }
  }

  &--free {
    background: var(--color-gray-50);
    color: var(--color-gray-400);
    border: 1.5px dashed var(--color-gray-300);
    cursor: default;
    text-align: center;

    &:hover {
      opacity: 1;
    }
  }
}

.sr-apt {
  font-family: var(--font-num);
  font-weight: var(--fw-bold);
}

.sr-apn {
  margin-top: 1px;
}

// ── Legenda ───────────────────────────────────────────────────────────────
.leg {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-xs);
  color: var(--color-gray-600);
}

.leg-swatch {
  width: 14px;
  height: 14px;
  border-radius: var(--radius-sm);
  flex: none;

  &--book {
    background: var(--color-primary-light);
    border-left: 3px solid var(--color-primary);
  }

  &--now {
    background: var(--color-success-light);
    border: 1.5px solid var(--color-success);
  }

  &--done {
    background: var(--color-gray-50);
    border-left: 3px solid var(--color-gray-300);
  }

  &--free {
    background: var(--color-gray-50);
    border: 1.5px dashed var(--color-gray-300);
  }
}

// ── Card configuração da agenda ───────────────────────────────────────────
.sr-config-card {
  padding: var(--space-5);
}

.sr-config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--space-4);
  margin-top: var(--space-3);
}

.sr-config-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.sr-config-label {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sr-config-value {
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-800);
}

// ── Modal horário pontual (dentro do b-modal) ─────────────────────────────
.pm-body {
  padding: var(--space-5) var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.pm-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

.pm-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.pm-label {
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-700);
}

.pm-input {
  height: 40px;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  padding: 0 var(--space-3);
  font-size: var(--font-size-sm);
  color: var(--color-gray-900);
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(26, 108, 247, 0.12);
  }
}

.pm-select {
  cursor: pointer;
}

.pm-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--color-gray-100);
}

.pm-slots-row {
  display: flex;
  gap: var(--space-2);
}

.pm-slot-btn {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
  background: var(--color-bg-page);
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-700);
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s, color 0.12s;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  &--active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
}

.pm-preview {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
}

// ── Modal reconfigurar ────────────────────────────────────────
.rc-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-6);
  text-align: center;
}

.rc-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #fef3c7;
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.rc-title {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-900);
}

.rc-text {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  line-height: 1.55;
}

.rc-footer {
  display: flex;
  gap: var(--space-3);
  width: 100%;
  justify-content: center;
}
</style>
